import { ArrowLeft, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useEffect, useState } from "react";
import connectingPeopleLogo from "@/assets/connecting-people-logo.png";
import playAbroadLogo from "@/assets/playabroad-logo.png";
import boschDigitalTwinLogo from "@/assets/bosch-digital-twin-logo.png";
import btcsLogo from "@/assets/btcs-logo.png";
import dieterHoetzerImg from "@/assets/dieter_hoetzer.jpg";
import jonasBischoffImg from "@/assets/jonas_bischoff.jpg";
import loganMeadowsImg from "@/assets/logan_meadows.jpg";
import fiveAiLogo from "@/assets/fiveai-logo.png";
import boschLogo from "@/assets/bosch-logo.png";
import frankBerrscheImg from "@/assets/frank_berrsche.jpg";
import kevin9 from "@/assets/kevin_9.jpg";
import CircularTestimonials from "@/components/ui/circular-testimonials";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { TranslationKey } from "@/translations/translations";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";

interface Company {
  name: string;
  description: string;
  url: string | null;
  logo?: string;
}

interface Testimonial {
  text: string;
  textFull?: string;
  author: string;
  role: string;
  image: string | { src: string };
}

const Perspectives = () => {
  const [expandedTestimonials, setExpandedTestimonials] = useState<Record<number, boolean>>({});
  const { t, language } = useLanguage();

  const companies: Company[] = [
    {
      name: t('investments.company.playAbroad.name' as TranslationKey),
      description: t('investments.company.playAbroad.description' as TranslationKey),
      logo: playAbroadLogo,
      url: "https://www.play-abroad.com/"
    },
    {
      name: t('investments.company.bosch.name' as TranslationKey),
      description: t('investments.company.bosch.description' as TranslationKey),
      logo: boschLogo,
      url: "https://www.bosch.com/"
    },
    {
      name: t('investments.company.boschDigitalTwin.name' as TranslationKey),
      description: t('investments.company.boschDigitalTwin.description' as TranslationKey),
      logo: boschDigitalTwinLogo,
      url: "https://www.bosch-digital-twin-industries.com/"
    },
    {
      name: t('investments.company.connectingPeople.name' as TranslationKey),
      description: t('investments.company.connectingPeople.description' as TranslationKey),
      logo: connectingPeopleLogo,
      url: "https://connecting-people.de/"
    },
    {
      name: t('investments.company.btcs.name' as TranslationKey),
      description: t('investments.company.btcs.description' as TranslationKey),
      logo: btcsLogo,
      url: "https://www.bosch.com/"
    },
    {
      name: t('investments.company.fiveAi.name' as TranslationKey),
      description: t('investments.company.fiveAi.description' as TranslationKey),
      logo: fiveAiLogo,
      url: "https://www.five.ai/"
    }
  ];

  const testimonials: Testimonial[] = [
    {
      text: t('investments.testimonial.dieter.text' as TranslationKey),
      textFull: t('investments.testimonial.dieter.text' as TranslationKey),
      author: t('investments.testimonial.dieter.author' as TranslationKey),
      role: t('investments.testimonial.dieter.role' as TranslationKey),
      image: dieterHoetzerImg
    },
    {
      text: t('investments.testimonial.jonas.text' as TranslationKey),
      textFull: t('investments.testimonial.jonas.text' as TranslationKey),
      author: t('investments.testimonial.jonas.author' as TranslationKey),
      role: t('investments.testimonial.jonas.role' as TranslationKey),
      image: jonasBischoffImg
    },
    {
      text: t('investments.testimonial.logan.text' as TranslationKey),
      textFull: t('investments.testimonial.logan.textFull' as TranslationKey),
      author: t('investments.testimonial.logan.author' as TranslationKey),
      role: t('investments.testimonial.logan.role' as TranslationKey),
      image: loganMeadowsImg
    },
    {
      text: t('investments.testimonial.frank.text' as TranslationKey),
      textFull: t('investments.testimonial.frank.textFull' as TranslationKey),
      author: t('investments.testimonial.frank.author' as TranslationKey),
      role: t('investments.testimonial.frank.role' as TranslationKey),
      image: frankBerrscheImg
    }
  ];

  const formattedTestimonials = testimonials.map(t => ({
    quote: t.textFull || t.text,
    name: t.author,
    designation: t.role,
    src: typeof t.image === 'string' ? t.image : t.image.src
  }));

  const testimonialLogos = companies.map(company => ({
    src: company.logo || "",
    name: company.name,
    description: company.description,
    url: company.url,
    testimonialIndex: testimonials.findIndex(t =>
      t.role.toLowerCase().includes(company.name.toLowerCase()) ||
      (company.name === "Five AI" && t.author.includes("Dieter"))
    ) > -1
      ? testimonials.findIndex(t =>
        t.role.toLowerCase().includes(company.name.toLowerCase()) ||
        (company.name === "Five AI" && t.author.includes("Dieter"))
      )
      : undefined
  }));

  // Re-order logos to put linked ones first if they are active, or keep original order.
  // Actually, let's keep the original order from the 'companies' array.

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, []);

  return (
    <main id="main-content" className="min-h-screen">
      <SEO
        title={t("seo.perspectives.title" as TranslationKey)}
        description={t("seo.perspectives.description" as TranslationKey)}
        keywords={t("seo.perspectives.keywords" as TranslationKey).split(",").map((k) => k.trim())}
      />
      <div className="container mx-auto px-2 sm:px-4 py-12">
        <Breadcrumbs />


        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fade-in">
            {t("investments.heading")}
          </h1>
          <p className="text-xl text-muted-foreground mb-4 animate-fade-in" style={{ animationDelay: "100ms" }}>
            {t("investments.description1")}
          </p>
          <p className="text-xl text-muted-foreground mb-12 animate-fade-in" style={{ animationDelay: "150ms" }}>
            {t("investments.description2")}
          </p>


          <hr className="my-12 border-primary/20" />

          <section className="animate-fade-in flex justify-center" aria-label="Testimonials" style={{ animationDelay: "350ms" }}>
            <CircularTestimonials
              testimonials={formattedTestimonials}
              logos={testimonialLogos}
              autoplay={true}
              readMoreLabel={t("investments.testimonial.readMore" as TranslationKey)}
              closeLabel={t("investments.testimonial.close" as TranslationKey)}
              visitWebsiteLabel={t("investments.visitWebsite" as TranslationKey)}
              colors={{
                name: "hsl(var(--foreground))",
                designation: "hsl(var(--muted-foreground))",
                testimony: "hsl(var(--muted-foreground))",
                arrowBackground: "hsl(var(--primary))",
                arrowForeground: "hsl(var(--primary-foreground))",
                arrowHoverBackground: "hsl(var(--accent))",
              }}
              fontSizes={{
                name: "1.75rem",
                designation: "1rem",
                quote: "1.125rem",
              }}
            />
          </section>

          <hr className="my-12 border-primary/20" />

          <section className="animate-fade-in mb-24" style={{ animationDelay: "400ms" }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold mb-6">
                  {t("investments.howIWork.heading" as TranslationKey)}
                </h2>
                <div className="space-y-4 text-muted-foreground text-lg leading-relaxed">
                  <p>{t("investments.howIWork.p1" as TranslationKey)}</p>
                  <p>{t("investments.howIWork.p2" as TranslationKey)}</p>
                  <p>{t("investments.howIWork.p3" as TranslationKey)}</p>
                </div>
              </div>
              <div className="order-1 md:order-2 flex justify-center">
                <div className="relative w-full max-w-md aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl border-4 border-primary/10 rotate-3 transition-transform hover:rotate-0 duration-500">
                  <img
                    src={kevin9}
                    alt={t("perspectives.image.howIWork.alt" as TranslationKey)}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>
      <Footer />
    </main>
  );
};

export default Perspectives;
